import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { useSnackbar } from "src/components/snackbar";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import {
  useEditarSolicitanteMutation,
  useLazyUsoSolicitanteQuery,
} from "src/services/propuestasApi";
import { BRAND } from "src/theme/brandTokens";

const FORM_INICIAL = { nombre: "", rut: "", telefono: "", correo: "" };

const emailValido = (valor) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor.trim());

/**
 * Diálogo para editar los datos de un solicitante existente.
 * Si el solicitante está vinculado a más de una propuesta, advierte
 * que los cambios afectarán a todas y pide confirmación antes de guardar.
 */
const EditarSolicitanteDialog = ({ open, onClose, solicitante, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(FORM_INICIAL);
  const [errores, setErrores] = useState({});

  const [editarSolicitante, { isLoading: guardando }] =
    useEditarSolicitanteMutation();
  const [triggerUso, { data: usoData }] = useLazyUsoSolicitanteQuery();

  const totalPropuestas = usoData?.totalPropuestas ?? 0;

  // Al abrir, precargar campos y consultar cuántas propuestas usan al solicitante
  useEffect(() => {
    if (open && solicitante?.id) {
      setForm({
        nombre: solicitante.nombre ?? "",
        rut: solicitante.rut ?? "",
        telefono: solicitante.telefono ?? "",
        correo: solicitante.correo ?? "",
      });
      setErrores({});
      triggerUso(solicitante.id);
    }
  }, [open, solicitante, triggerUso]);

  const handleCampo = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
    setErrores((prev) => ({ ...prev, [campo]: undefined }));
  };

  const validar = () => {
    const nuevos = {};
    if (!form.nombre.trim()) {
      nuevos.nombre = "El nombre es requerido.";
    }
    if (!form.correo.trim()) {
      nuevos.correo = "El correo es requerido.";
    } else if (!emailValido(form.correo)) {
      nuevos.correo = "Ingresa un correo válido.";
    }
    setErrores(nuevos);
    return Object.keys(nuevos).length === 0;
  };

  const handleGuardar = async () => {
    if (!validar()) return;

    // Si el solicitante está en más de una propuesta, pedir confirmación explícita
    if (totalPropuestas > 1) {
      const result = await AlertaModalV2({
        title: "¿Actualizar solicitante compartido?",
        text: `Este contacto está vinculado a ${totalPropuestas} propuestas. Los cambios se aplicarán a todas.`,
        confirmButtonText: "Sí, actualizar",
        cancelButtonText: "Cancelar",
        icon: "warning",
        confirmButtonColor: BRAND.color.primary,
      }).fire();
      if (!result.isConfirmed) return;
    }

    try {
      const body = {
        nombre: form.nombre.trim(),
        correo: form.correo.trim(),
      };
      if (form.rut.trim()) body.rut = form.rut.trim();
      if (form.telefono.trim()) body.telefono = form.telefono.trim();

      const actualizado = await editarSolicitante({
        id: solicitante.id,
        ...body,
      }).unwrap();

      enqueueSnackbar("Solicitante actualizado.", { variant: "success" });
      onSaved?.(actualizado);
      onClose();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al actualizar el solicitante.",
        { variant: "error" },
      );
    }
  };

  const handleCerrar = () => {
    if (!guardando) {
      setForm(FORM_INICIAL);
      setErrores({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Editar solicitante</Typography>
        <IconButton size="small" onClick={handleCerrar} disabled={guardando}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {totalPropuestas > 1 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este contacto se usa en {totalPropuestas} propuestas; los cambios se
            aplicarán a todas.
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre *"
                value={form.nombre}
                onChange={handleCampo("nombre")}
                error={!!errores.nombre}
                helperText={errores.nombre}
                disabled={guardando}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RUT (opcional)"
                value={form.rut}
                onChange={handleCampo("rut")}
                disabled={guardando}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Correo *"
                type="email"
                value={form.correo}
                onChange={handleCampo("correo")}
                error={!!errores.correo}
                helperText={errores.correo}
                disabled={guardando}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono (opcional)"
                value={form.telefono}
                onChange={handleCampo("telefono")}
                disabled={guardando}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <LoadingButton
          variant="contained"
          loading={guardando}
          onClick={handleGuardar}
          sx={{
            bgcolor: BRAND.color.primary,
            "&:hover": { bgcolor: BRAND.color.primaryDark },
          }}
        >
          Guardar cambios
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EditarSolicitanteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  solicitante: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre: PropTypes.string,
    rut: PropTypes.string,
    telefono: PropTypes.string,
    correo: PropTypes.string,
  }),
  onSaved: PropTypes.func,
};

EditarSolicitanteDialog.defaultProps = {
  solicitante: null,
  onSaved: undefined,
};

export default EditarSolicitanteDialog;
